import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminApi, getToken, clearToken, API } from "@/lib/api";
import { toast } from "sonner";
import { Download, LogOut, ImageIcon, Video, Users, FileText, Calendar } from "lucide-react";

const STATUS_COLORS = {
  "NEW": "bg-butter text-ink",
  "REVIEWED": "bg-stone-200 text-ink",
  "USED IN BOOK": "bg-emerald-200 text-emerald-900",
  "USED IN VIDEO": "bg-sky-200 text-sky-900",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deadline, setDeadline] = useState("");
  const [savingDeadline, setSavingDeadline] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!getToken()) { nav("/admin/login"); return; }
    load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const [s, l, d] = await Promise.all([
        adminApi.get("/admin/stats"),
        adminApi.get("/admin/submissions"),
        adminApi.get("/config/deadline"),
      ]);
      setStats(s.data);
      setSubs(l.data);
      setDeadline(d.data.deadline ? d.data.deadline.slice(0, 10) : "");
    } catch (e) {
      if (e.response?.status === 401) { clearToken(); nav("/admin/login"); return; }
      toast.error("Couldn't load the vault.");
    } finally {
      setLoading(false);
    }
  }

  async function saveDeadline() {
    setSavingDeadline(true);
    try {
      await adminApi.put("/admin/config/deadline", { deadline: deadline ? new Date(deadline).toISOString() : null });
      toast.success("Deadline updated.");
    } catch (e) {
      toast.error("Couldn't save deadline.");
    } finally {
      setSavingDeadline(false);
    }
  }

  function downloadAll() {
    const url = `${API}/admin/download-all?auth=${encodeURIComponent(getToken())}`;
    window.open(url, "_blank");
  }

  function logout() { clearToken(); nav("/admin/login"); }

  return (
    <div className="min-h-screen bg-cream text-ink" data-testid="admin-dashboard">
      <div className="border-b border-ink/15 bg-ink text-cream">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.4em] text-cream/60">OPERATION: JAMES TURNS 40</div>
            <div className="font-display text-3xl leading-tight">The Vault</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={downloadAll} className="inline-flex items-center gap-2 bg-butter text-ink rounded-full px-4 py-2 text-sm hover:bg-butterdark" data-testid="download-all-btn">
              <Download className="w-4 h-4" /> Download all
            </button>
            <button onClick={logout} className="inline-flex items-center gap-2 border border-cream/30 rounded-full px-4 py-2 text-sm hover:bg-cream/10" data-testid="admin-logout">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10">
        {loading ? <div>Loading…</div> : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stats-cards">
              <StatCard icon={<FileText className="w-5 h-5" />} label="Submissions" value={stats?.submissions ?? 0} testid="stat-submissions" />
              <StatCard icon={<Users className="w-5 h-5" />} label="People" value={stats?.people ?? 0} testid="stat-people" />
              <StatCard icon={<ImageIcon className="w-5 h-5" />} label="Photos" value={stats?.photos ?? 0} testid="stat-photos" />
              <StatCard icon={<Video className="w-5 h-5" />} label="Videos" value={stats?.videos ?? 0} testid="stat-videos" />
            </div>

            <div className="mt-8 bg-white border border-ink/15 rounded-2xl p-6 flex flex-col md:flex-row md:items-end gap-4">
              <div className="flex-1">
                <label className="text-xs tracking-[0.3em] text-ink/60 flex items-center gap-2"><Calendar className="w-4 h-4" /> SUBMISSION DEADLINE</label>
                <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)}
                  className="mt-2 border border-ink/20 rounded-xl px-3 py-2 bg-cream/60"
                  data-testid="deadline-input" />
              </div>
              <button onClick={saveDeadline} disabled={savingDeadline}
                className="bg-ink text-cream rounded-full px-6 py-3 hover:bg-ink/90 disabled:opacity-60"
                data-testid="save-deadline-btn">
                {savingDeadline ? "Saving…" : "Save deadline"}
              </button>
            </div>

            <h2 className="mt-12 font-display text-3xl md:text-4xl" data-testid="submissions-heading">Submissions</h2>
            {subs.length === 0 ? (
              <div className="mt-6 text-ink/60 font-hand text-2xl">Nothing in the vault yet.</div>
            ) : (
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="submissions-list">
                {subs.map((s, i) => (
                  <Link key={s.id} to={`/admin/submissions/${s.id}`}
                    className={`bg-white border border-ink/15 rounded-2xl p-6 hover:shadow-lg transition-all block ${i % 2 === 0 ? "hover:-rotate-1" : "hover:rotate-1"}`}
                    data-testid={`submission-card-${i}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                <div className="text-xs tracking-[0.3em] text-ink/60">{new Date(s.submitted_at).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}</div>
                        <div className="font-display text-2xl md:text-3xl mt-1">{s.name}</div>
                        {s.relationship ? <div className="font-hand text-lg text-ink/70">{s.relationship}</div> : null}
                      </div>
                      <span className={`text-[10px] tracking-[0.2em] px-2 py-1 rounded-full ${STATUS_COLORS[s.status] || STATUS_COLORS.NEW}`}>{s.status}</span>
                    </div>
                    <div className="mt-4 text-sm text-ink/70 flex flex-wrap gap-3">
                      {s.message ? <span>✓ Message</span> : null}
                      <span>{s.photos?.length || 0} photos</span>
                      <span>{s.video ? "1 video" : "no video"}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, testid }) {
  return (
    <div className="bg-white border border-ink/15 rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs tracking-[0.25em] text-ink/60">{icon} {label}</div>
      <div className="font-display text-4xl mt-2">{value}</div>
    </div>
  );
}
